import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicNav, { PublicFooter } from "../components/PublicNav";
import { apiFetch } from "../services/api";
import { User } from "../types";
export function PlanCards({ user }: { user?: User | null }) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(""),
    [code, setCode] = useState("SALES120");
  const navigate = useNavigate();
  const checkout = async (plan: "monthly" | "yearly") => {
    if (!user) {
      navigate(`/signup?plan=${plan}`);
      return;
    }
    setBusy(plan);
    setError("");
    try {
      const r = await apiFetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan,
          ...(plan === "yearly" && code.trim()
            ? { promotion_code: code.trim() }
            : {}),
        }),
      });
      const data = await r.json();
      if (!r.ok)
        throw new Error(
          typeof data.error === "string"
            ? data.error
            : "Unable to open checkout.",
        );
      window.location.assign(data.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to connect.");
      setBusy("");
    }
  };
  return (
    <>
      <div className="plan-grid">
        <article className="plan-card">
          <span className="eyebrow">STAY FLEXIBLE</span>
          <h2>Monthly</h2>
          <p>Build your pipeline at your own pace.</p>
          <div className="plan-price">
            $40<span>/ month</span>
          </div>
          <div className="plan-allowance">
            <strong>5,000</strong> contact unlocks each month{" "}
            <span className="mini-tag">Contact credits included</span>
          </div>
          <button
            className="button button-outline full"
            disabled={!!busy}
            onClick={() => void checkout("monthly")}
          >
            {busy === "monthly" ? "Opening checkout…" : "Choose monthly"}{" "}
            <span>↗</span>
          </button>
          <ul className="check-list">
            <li>Search the full lead database</li>
            <li>All available fields with each unlock</li>
            <li>Saved lists, searches, and private notes</li>
            <li>CSV exports of unlocked contacts</li>
            <li>Cancel through your billing portal</li>
          </ul>
          <p className="plan-fine">
            $40 billed monthly. Discounts apply to annual billing only.
          </p>
        </article>
        <article className="plan-card featured">
          <span className="plan-ribbon">ANNUAL · UP TO 50% OFF FIRST YEAR</span>
          <span className="eyebrow">PLAN FOR THE LONG RUN</span>
          <h2>Annual</h2>
          <p>A full year of opportunities, one payment.</p>
          <div className="plan-price">
            $240<span>/ year</span>
          </div>
          <p className="offer-line">
            <strong>$120 for your first year</strong> with SALES120
          </p>
          <div className="plan-allowance">
            <strong>60,000</strong> contact unlocks upfront{" "}
            <span className="mini-tag">Contact credits included</span>
          </div>
          <button
            className="button button-dark full"
            disabled={!!busy}
            onClick={() => void checkout("yearly")}
          >
            {busy === "yearly" ? "Opening checkout…" : "Choose annual"}{" "}
            <span>↗</span>
          </button>
          <ul className="check-list">
            <li>Everything included in Monthly</li>
            <li>All 60,000 credits available after payment</li>
            <li>
              Save 50% against 12 monthly payments at the standard annual price
            </li>
            <li>
              Eligible first-time customers get another 50% off their first
              annual payment
            </li>
          </ul>
          <label className="promo-label" htmlFor="annual-promo">
            Annual promotion code (optional)
          </label>
          <input
            id="annual-promo"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Promotion code"
          />
          <p className="plan-fine">
            SALES120: eligible first-time customers pay $120 today, then
            $240/year. Clear the code to pay the standard annual price. Cancel
            before renewal.
          </p>
        </article>
      </div>
      {error && (
        <div role="alert" className="notice error">
          {error}
        </div>
      )}
      <p className="pricing-footnote">
        1 credit = 1 contact unlock. Reopening or exporting an unlocked contact
        costs no additional credits. Unused credits carry forward while your
        subscription stays active. Contact availability varies by search.
      </p>
    </>
  );
}
export default function PricingPage({ user }: { user: User | null }) {
  return (
    <div className="public-site">
      <PublicNav />
      <main className="public-section pricing-page">
        <div className="section-heading">
          <span className="eyebrow">SIMPLE PLANS. MORE POSSIBILITIES.</span>
          <h1>
            Your pipeline.
            <br />A little more room to grow.
          </h1>
          <p>
            Two plans. More contact credits. No extra export charges.
          </p>
        </div>
        <PlanCards user={user} />
        <div className="faq-grid">
          <div>
            <h3>When do I receive credits?</h3>
            <p>
              After Stripe confirms payment: 5,000 each monthly renewal or
              60,000 each annual renewal.
            </p>
          </div>
          <div>
            <h3>Can I download my contacts?</h3>
            <p>
              Yes. Export your unlocked contacts without additional credit
              charges, including after cancellation.
            </p>
          </div>
          <div>
            <h3>What can a discount apply to?</h3>
            <p>
              Annual plans only, up to 50% off the first year. Monthly plans
              stay at $40.
            </p>
          </div>
          <div>
            <h3>Need a hand?</h3>
            <p>
              <a href="mailto:support@reachatlasleads.com">
                support@reachatlasleads.com
              </a>
            </p>
          </div>
        </div>
        <Link to="/explore">Explore the database before choosing a plan →</Link>
      </main>
      <PublicFooter />
    </div>
  );
}
